import { createAsyncThunk } from '@reduxjs/toolkit';
import { removeCache, updateCache } from './cache';

const createCacheKey = (type, arg) => {
    if (!arg) return type;

    if (typeof arg === 'string' || typeof arg === 'number') {
        return `${type}:${arg}`;
    }

    if (typeof arg === 'object') {
        const keys = Object.keys(arg).sort();
        const parts = keys.map((k) => `${k}=${arg[k]}`);
        return `${type}:${parts.join('&')}`;
    }

    return type;
};

export const createAsyncThunkWithCaching = (type, thunk, options = {}) => {
    const {
        cacheEnabled = true,
        updateCacheFromCaller = false,
        customCacheKey = null,
    } = options;

    return createAsyncThunk(type, async (arg, thunkAPI) => {
        const shouldCache = cacheEnabled;
        const cacheKey = customCacheKey || createCacheKey(type, arg);
        const RejectWithValue = thunkAPI.rejectWithValue().constructor;

        try {
            if (shouldCache) {
                const cache = thunkAPI.getState().cache.cache[cacheKey];

                if (cache && cache.timestamp + 60000 > Date.now()) {
                    if (
                        cache.data &&
                        !isErrorObject(cache.data) &&
                        !(cache.data instanceof RejectWithValue) &&
                        !isStoredRejection(cache.data)
                    ) {
                        return cache.data;
                    }
                    thunkAPI.dispatch(removeCache({ key: cacheKey }));
                }
            }

            const response = await thunk(arg, thunkAPI);

            if (response instanceof RejectWithValue) {
                return response;
            }

            if (isErrorObject(response)) {
                console.error('Thunk error:', type, response);
                return thunkAPI.rejectWithValue({
                    message: response.message || 'Request failed',
                });
            }

            if (
                shouldCache &&
                !updateCacheFromCaller &&
                isValidDataForCaching(response)
            ) {
                thunkAPI.dispatch(
                    updateCache({
                        key: cacheKey,
                        data: response,
                    }),
                );
            }

            return response;
        } catch (error) {
            console.error(`${type} error:`, error);
            return thunkAPI.rejectWithValue({
                message: error.message || 'Unknown error',
                status: error.response?.status || 500,
            });
        }
    });
};

const isErrorObject = (obj) => {
    if (!obj || typeof obj !== 'object') return false;
    return !!(
        obj.stack ||
        obj.name === 'TypeError' ||
        obj.name === 'Error' ||
        obj.name === 'AxiosError' ||
        obj.isAxiosError ||
        (obj.constructor && obj.constructor.name === 'Error')
    );
};

const isStoredRejection = (data) => {
    if (typeof data !== 'object' || Array.isArray(data)) return false;
    const keys = Object.keys(data);
    return (
        keys.includes('payload') &&
        keys.every((key) => key === 'payload' || key === 'meta')
    );
};

const isValidDataForCaching = (data) => {
    if (data === null || data === undefined) return false;
    if (isErrorObject(data)) return false;

    const type = typeof data;
    if (type === 'function' || type === 'symbol') return false;

    if (Array.isArray(data) && data.length > 0) return true;

    if (type === 'object') {
        try {
            JSON.stringify(data);
            return true;
        } catch {
            return false;
        }
    }

    return true;
};
